import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Flame, Leaf, Clock, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BottomSheet } from "@/components/BottomSheet";
import { AllergenPills } from "@/components/Badges";
import { useUI } from "@/context/UIContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { getProductById } from "@/data/menu";
import { getMeta } from "@/data/productMeta";
import { localize, localizeList, formatTRY, computeLinePrice, defaultSelections } from "@/lib/format";
import { computeNutrition, computeAllergens } from "@/lib/nutrition";
import { UpsellSuggestions } from "@/components/UpsellSuggestions";

export function ProductDetailSheet() {
  const { productSheetId, editLineId, closeProduct } = useUI();
  const { addItem, updateLine, detailed } = useCart();
  const { lang, t } = useLang();
  const product = productSheetId ? getProductById(productSheetId) : null;

  const [selections, setSelections] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!product) return;
    if (editLineId) {
      const line = detailed.find((l) => l.lineId === editLineId);
      if (line) {
        setSelections(line.selections || defaultSelections(product));
        setQuantity(line.quantity);
        setNote(line.note || "");
        return;
      }
    }
    setSelections(defaultSelections(product));
    setQuantity(1);
    setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSheetId, editLineId]);

  const unitPrice = useMemo(
    () => (product ? computeLinePrice(product, selections) : 0),
    [product, selections]
  );

  const isValid = useMemo(() => {
    if (!product) return false;
    return (product.modifierGroups || []).every((g) => {
      if (!g.required) return true;
      return (selections[g.id] || []).length >= (g.min || 1);
    });
  }, [product, selections]);

  const liveNutrition = useMemo(() => (product ? computeNutrition(product, selections) : null), [product, selections]);
  const liveAllergens = useMemo(() => (product ? computeAllergens(product, selections) : []), [product, selections]);

  if (!product) return <BottomSheet open={false} onClose={closeProduct} />;

  const toggleOption = (group, optId) => {
    setSelections((prev) => {
      const current = prev[group.id] || [];
      if (group.max === 1) return { ...prev, [group.id]: [optId] };
      if (current.includes(optId)) return { ...prev, [group.id]: current.filter((x) => x !== optId) };
      if (current.length >= group.max) return prev;
      return { ...prev, [group.id]: [...current, optId] };
    });
  };

  const handleSubmit = () => {
    const payload = { productId: product.id, quantity, selections, note };
    if (editLineId) {
      updateLine(editLineId, payload);
      toast.success(t("edit"));
    } else {
      addItem(payload);
      toast.success(`${localize(product.name, lang)} — ${t("addToCart")}`);
    }
    closeProduct();
  };

  const suggestionIds = getMeta(product.id).suggestions || [];
  const ingredients = localizeList(product.ingredients, lang);

  return (
    <BottomSheet open={!!productSheetId} onClose={closeProduct} testId="product-detail-sheet">
      <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-5">
        <div className="relative -mx-5 mb-4 h-56 overflow-hidden bg-muted">
          <motion.img
            layoutId={`product-img-${product.id}`}
            src={product.image}
            alt={localize(product.name, lang)}
            className="h-full w-full object-cover"
          />
          <button
            onClick={closeProduct}
            className="absolute end-4 top-2 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white backdrop-blur active:scale-90"
            aria-label={t("close")}
            data-testid="product-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-start justify-between gap-3">
          <h2 className="font-heading text-2xl font-extrabold leading-tight">{localize(product.name, lang)}</h2>
          <span className="shrink-0 font-heading text-xl font-extrabold text-primary">
            {formatTRY(product.basePrice)}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {product.spicy && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-600 dark:text-red-400">
              <Flame className="h-3.5 w-3.5" /> {t("spicy")}
            </span>
          )}
          {product.vegetarian && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-semibold text-green-600 dark:text-green-400">
              <Leaf className="h-3.5 w-3.5" /> {t("vegetarian")}
            </span>
          )}
          {product.prepTimeMin > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {product.prepTimeMin} {t("min")}
            </span>
          )}
        </div>

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {localize(product.description, lang)}
        </p>

        {/* Ingredients */}
        {ingredients.length > 0 && (
          <div className="mt-5">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t("ingredients")}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {ingredients.map((ing) => (
                <span key={ing} className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">
                  {ing}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Allergens (live) */}
        {liveAllergens.length > 0 && (
          <div className="mt-5">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t("allergens")}
            </h3>
            <AllergenPills allergens={liveAllergens} />
          </div>
        )}

        {/* Nutrition (live, updates with selections) — only when the business supplies it */}
        {product.nutrition && (
        <div className="mt-5">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t("nutrition")}
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {[
              { v: `${liveNutrition.calories}`, l: t("calories") },
              { v: `${liveNutrition.protein}g`, l: t("protein") },
              { v: `${liveNutrition.carbs}g`, l: t("carbs") },
              { v: `${liveNutrition.fat}g`, l: t("fat") },
            ].map((x) => (
              <div key={x.l} className="rounded-xl border border-border bg-card p-2.5 text-center">
                <motion.div key={x.v} initial={{ scale: 0.8, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }} className="font-heading text-base font-extrabold">
                  {x.v}
                </motion.div>
                <div className="text-[10px] text-muted-foreground">{x.l}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">{t("nutritionNote")}</p>
        </div>
        )}

        {/* Modifier groups */}
        {(product.modifierGroups || []).map((group) => {
          const chosen = selections[group.id] || [];
          return (
            <div key={group.id} className="mt-6" data-testid={`modifier-group-${group.id}`}>
              <div className="mb-2.5 flex items-center justify-between">
                <h3 className="font-heading text-base font-bold">{localize(group.name, lang)}</h3>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    group.required ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {group.required ? t("required") : t("optional")}
                </span>
              </div>
              <div className="space-y-2">
                {group.options.map((opt) => {
                  const selected = chosen.includes(opt.id);
                  const single = group.max === 1;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleOption(group, opt.id)}
                      className={`flex w-full items-center gap-3 rounded-2xl border p-3.5 text-start transition-colors active:scale-[0.99] ${
                        selected ? "border-primary bg-primary/5" : "border-border bg-card"
                      }`}
                      data-testid={`modifier-option-${opt.id}`}
                    >
                      <span
                        className={`grid h-5 w-5 shrink-0 place-items-center border-2 transition-colors ${
                          single ? "rounded-full" : "rounded-md"
                        } ${selected ? "border-primary bg-primary text-white" : "border-muted-foreground/30"}`}
                      >
                        {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="flex-1 text-sm font-medium">{localize(opt.name, lang)}</span>
                      {opt.priceDelta > 0 && (
                        <span className="text-sm font-semibold text-muted-foreground">
                          +{formatTRY(opt.priceDelta)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Special note */}
        <div className="mt-6">
          <h3 className="mb-2 font-heading text-base font-bold">{t("specialRequest")}</h3>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("specialRequestPh")}
            className="min-h-[76px] resize-none rounded-2xl"
            data-testid="product-note-input"
          />
        </div>

        {/* Cross-sell */}
        {suggestionIds.length > 0 && (
          <div className="mt-6">
            <UpsellSuggestions ids={suggestionIds} testId="product-upsell" />
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="shrink-0 border-t border-border bg-card p-4 pb-safe">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-full border border-border bg-background p-1">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="grid h-10 w-10 place-items-center rounded-full text-foreground active:scale-90 disabled:opacity-40"
              disabled={quantity <= 1}
              aria-label={t("decrease")}
              data-testid="qty-decrease-btn"
            >
              <Minus className="h-5 w-5" />
            </button>
            <span className="w-7 text-center font-heading text-lg font-extrabold" data-testid="qty-value">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="grid h-10 w-10 place-items-center rounded-full text-foreground active:scale-90"
              aria-label={t("increase")}
              data-testid="qty-increase-btn"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!isValid}
            className="h-14 flex-1 rounded-full brand-gradient text-base font-bold text-white shadow-soft disabled:opacity-50"
            data-testid="add-to-cart-btn"
          >
            {t("addToCart")} · {formatTRY(unitPrice * quantity)}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
